import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    PlaydateInviteConnection,
    PlaydateInviteEdge,
    PageInfo,
    User,
    Connection,
} from "../../../gql/types";
import styles from "../Inbox.module.css";
import Spinner from "../../../components/button/Spinner";
import PlaydateInviteOutboundUnit from "../../../components/playdate/PlaydateInviteOutbountUnit";
import NullStateView from "./NullStateView";

interface PlaydateInviteOutboundViewProps {
    user?: User;
    /** Optional pre-fetched invites to show immediately. */
    connectionData?: PlaydateInviteConnection;
    /** Whether this component is the active/visible tab. */
    targetConnection?: Connection;
    loading: boolean;
    fetchMore: Function;
    onResponse: () => void;
    active: boolean;
}

const PlaydateInviteOutboundView: React.FC<PlaydateInviteOutboundViewProps> = ({
    user,
    connectionData,
    targetConnection,
    loading,
    fetchMore,
    onResponse,
    active,
}) => {
    const [edges, setEdges] = useState<PlaydateInviteEdge[]>(
        connectionData?.edges || []
    );
    const [pageInfo, setPageInfo] = useState<PageInfo | null>(
        connectionData?.pageInfo || null
    );
    const sentinelRef = useRef<HTMLDivElement>(null);

    // Update state when the parent-provided connectionData changes.
    useEffect(() => {
        if (connectionData) {
            setEdges(connectionData.edges);
            setPageInfo(connectionData.pageInfo);
        }
    }, [connectionData]);

    // Pagination: load more invites if there is a next page.
    const loadMore = useCallback(async () => {
        if (!pageInfo?.hasNextPage || loading) return;
        try {
            const { data: moreData } = await fetchMore({
                variables: {
                    cursor: pageInfo.endCursor,
                },
            });
            if (moreData?.listInboxInvites) {
                setEdges((prevEdges) => [
                    ...prevEdges,
                    ...moreData.listInboxInvites.edges,
                ]);
                setPageInfo(moreData.listInboxInvites.pageInfo);
            }
        } catch (err) {
            console.error("Error fetching more invites:", err);
        }
    }, [pageInfo, fetchMore, loading]);

    // Set up an IntersectionObserver on the sentinel element.
    useEffect(() => {
        if (!sentinelRef.current || !pageInfo?.hasNextPage) return;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        loadMore();
                    }
                });
            },
            { threshold: 1.0 }
        );

        observer.observe(sentinelRef.current);
        return () => {
            if (sentinelRef.current) {
                observer.unobserve(sentinelRef.current);
            }
        };
    }, [sentinelRef, pageInfo, loadMore]);

    if (!active) {
        return null;
    }

    if (loading && edges.length === 0) {
        return (
            <div className={styles.fullPageCenteredContainer}>
                <Spinner color="brown" />
            </div>
        );
    }

    if (edges.length === 0) {
        return (
            <NullStateView
                onCreate={onResponse}
                connections={targetConnection ? [targetConnection] : undefined}
            />
        );
    }

    return (
        <div className={`${styles.inboundContainer} ${active ? "" : styles.inactive}`}>
            <div className={styles.requestsInitialPadder}></div>
            {edges.map((edge) => (
                <PlaydateInviteOutboundUnit key={edge.cursor} user={user} invite={edge.node} onResponse={onResponse} />
            ))}
            {pageInfo?.hasNextPage && (
                <div ref={sentinelRef} className={styles.sentinel}>
                    {loading && <Spinner color="brown" />}
                </div>
            )}
            <div className={styles.requestsEndPadder}></div>
        </div>
    );
};

export default PlaydateInviteOutboundView;